import config from '../../config';

import manageAvailabilityComponentUrl from './manageAvailabilityComponent.html';

class ManageAvailabilityCtrl {
  constructor(
    Authentication,
    UserUtils,
    Utils,
    ShiftHeaderUtils,
    AvailabilityUtils,
    sdk,
    dateFilter,
    toTimeShortFilter,
    $element,
    $scope
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      UserUtils,
      Utils,
      ShiftHeaderUtils,
      AvailabilityUtils,
      sdk,
      dateFilter,
      toTimeShortFilter,
      $element,
      $scope,
    });
  }

  $onInit() {
    this.init();
    this.hours = this._initHours();
    this.quantum = 15;

    const { visibleHoursStart, visibleHoursEnd } = this.Authentication.user.orgSettings;
    this.visibleHours = this.Utils.getVisibleHoursRange(visibleHoursStart, visibleHoursEnd);
    // Because availability grid renders diffrently then shifts we have to make sure we have room for minutes on last hour
    const lastVisibileHour = this.visibleHours[this.visibleHours.length - 1] + 1;
    this.visibleHours.push(lastVisibileHour);
    this.gridSize = this.visibleHours.length;

    this.weekdays = this.sdk('constants').WEEKDAYS;
    this.intervalOptions = this.sdk('constants').UNAVAILABILITY_INTERVALS;
    this.repeatNeverValue = this.sdk('constants').UNAVAILABILITY_NO_REPEAT_VALUE;

    let firstDayOfWeek = this.Authentication.user?.orgSettings.firstDayOfWeek;
    if (typeof firstDayOfWeek !== 'number') {
      firstDayOfWeek = 1;
    }

    while (this._getDayFromWeekday(this.weekdays[0]) % 7 !== firstDayOfWeek) {
      this.weekdays = this._rotateLeftWithCarry(this.weekdays);
    }

    this.middleDay = this.weekdays[3];
    this.newSlots = this._initNewSlots();

    this.untilDatepickerAddon = {
      action: () => {
        this.availability.details.until = null;
        this.openedUntilPicker = false;
        this.notifyChange();
      },
      show: () => this.availability.details.until,
      template: `
        <button type="button" class="btn btn-cancel btn-block margin-top-10" ng-click="datepickerAddon.action()" ng-show="datepickerAddon.show()">
          Indefinitely
        </button>
      `,
    };

    this.fromDatepickerOptions = this.getFromDatepickerOptions();
    this.untilDatepickerOptions = {};

    this.setIsEmpty();

    this.$scope.$watch('ctrl.availability.details.start', (startDate) => {
      this.untilDatepickerOptions.initDate = new Date(startDate);
      this.untilDatepickerOptions.minDate = new Date(startDate);
    });
  }

  async init() {
    this.extraUserData = await this.UserUtils.getExtraUserData(this.Authentication.user);
  }

  $doCheck() {
    this.setIsEmpty();
  }

  get canEdit() {
    return (
      !this.readOnly && !this.isEditing && this.AvailabilityUtils.isEditable(this.availability)
    );
  }

  get canClear() {
    return this.isEditing && !this.AvailabilityUtils.isApprovalEnabled();
  }

  get canCancel() {
    if (this.singleSet) {
      return this.isEditing && this.availability.availabilityId;
    }

    return this.isEditing;
  }

  get canDelete() {
    return this.availability.availabilityId && !this.showRequestActions;
  }

  get showCancelRequestLabel() {
    return (
      !this.Authentication.user.isManager && this.AvailabilityUtils.isRequest(this.availability)
    );
  }

  get canSkipRequest() {
    return this.Authentication.user.isManager || !this.AvailabilityUtils.isApprovalEnabled();
  }

  get showRequestActions() {
    return (
      !this.isEditing &&
      this.Authentication.user.isManager &&
      this.AvailabilityUtils.isRequest(this.availability) &&
      this.canApproveSet()
    );
  }

  get isRequest() {
    return this.AvailabilityUtils.isRequest(this.availability);
  }

  get isCancelled() {
    return this.availability.details.cancelled;
  }

  get showUntil() {
    return this.availability.details.interval !== this.repeatNeverValue;
  }

  get hideOptions() {
    return config.UNAVAILABILITY_LIST_V2_ENABLED && !this.availability.availabilityId;
  }

  get hideCopy() {
    return config.UNAVAILABILITY_LIST_V2_ENABLED;
  }

  canApproveSet() {
    const isSetCreator = this.Authentication.user.id === this.availability.details.user.id;
    const hasManager = this.extraUserData?.managers.length !== 0;
    return !(isSetCreator && hasManager);
  }

  setIsEmpty() {
    this.isEmpty = this.availability.isEmpty();
  }

  getSlotStyle(slot, slotsContainer, weekday) {
    const availability = this.availability.unavailabilities[weekday];
    const style = this.getUnavailabilityStyle(slot, slotsContainer);

    if (this.isEditing && this.noExistingSlotDuringRange(slot, availability)) {
      return style;
    }

    if (slot.halfStart || slot.halfEnd) {
      const width = getValueFromString(style.width) / 2;
      const left = slot.halfEnd ? getValueFromString(style.left) + width : undefined;

      return {
        ...style,
        width: `${width}px`,
        left: left ? `${left}px` : style.left,
      };
    }
    return { display: 'none' };

    function getValueFromString(val) {
      return Number(val.slice(0, -2));
    }
  }

  getUnavailabilityStyle(unavailability, slotsContainer) {
    const businessDay = this.Utils.getVisibleHoursBoundaries(
      this.Authentication.user.orgSettings.visibleHoursStart,
      this.Authentication.user.orgSettings.visibleHoursEnd,
      unavailability.dtstart
    );
    let durationInMinutes;
    let visibleStartMinutes;

    if (this._isOutsideBusinessDay(businessDay, unavailability)) {
      return { display: 'none' };
    }

    if (unavailability.fullDay) {
      durationInMinutes = (this.hours.length - 1) * 60;
      visibleStartMinutes = 0;
    } else {
      const visibleMinutesGap = businessDay.visibleStart.getHours() * 60;
      const startMinutes = this._calculateMinutes(unavailability.dtstart);

      visibleStartMinutes = startMinutes;
      if (unavailability.dtstart.getDate() === businessDay.visibleStart.getDate()) {
        visibleStartMinutes =
          startMinutes >= visibleMinutesGap ? startMinutes - visibleMinutesGap : 0;
      } else {
        visibleStartMinutes = startMinutes + (24 * 60 - visibleMinutesGap);
      }

      const start =
        unavailability.dtstart > businessDay.visibleStart
          ? unavailability.dtstart
          : businessDay.visibleStart;
      const end =
        unavailability.dtend < businessDay.visibleEnd
          ? unavailability.dtend
          : businessDay.visibleEnd;

      durationInMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    }

    const singleSlotWidth = slotsContainer[0].clientWidth / (this.visibleHours.length - 1);
    const quantum = singleSlotWidth / 60;

    const style = {
      left: `${visibleStartMinutes * quantum + 2}px`,
      width: `${durationInMinutes * quantum + 2 - 2 * 2}px`,
      zIndex: unavailability.fullDay ? 2 : 1,
    };

    if (!durationInMinutes) {
      style.display = 'none';
    }

    return style;
  }

  _isOutsideBusinessDay(businessDay, unavailability) {
    if (unavailability.fullDay) {
      return false;
    }
    return (
      businessDay.visibleStart > unavailability.dtend ||
      businessDay.visibleEnd < unavailability.dtstart
    );
  }

  noExistingSlotDuringRange(slot, daySlots) {
    let noConflict = true;
    let slotStart; // eslint-disable-line
    let slotEnd;

    if (daySlots && daySlots.length) {
      noConflict = !daySlots.some((daySlot) => {
        slotStart = new Date(daySlot.dtstart);
        slotStart.setHours(slot.dtstart.getHours());
        slotStart.setMinutes(slot.dtstart.getMinutes());

        slotEnd = new Date(daySlot.dtstart);
        slotEnd.setHours(slot.dtend.getHours());
        slotEnd.setMinutes(slot.dtend.getMinutes());

        if (slotEnd.getHours() === 0) {
          slotEnd = this.Utils.addDays(slotEnd, 1);
        }

        const daySlotStart = daySlot.dtstart;
        const daySlotEnd = daySlot.dtend;

        const hasConflict =
          (slotEnd > daySlotStart && slotStart < daySlotStart) ||
          (slotStart < daySlotEnd && slotEnd > daySlotStart);

        if (hasConflict) {
          // if diff is 30 minutes, add half of the button to beginning or end of slot
          slot.halfStart = this._getTimeDifferenceInMinutes(daySlotStart, slotStart) === 30;
          slot.halfEnd = this._getTimeDifferenceInMinutes(slotEnd, daySlotEnd) === 30;
        }

        return hasConflict;
      });
    }
    return noConflict;
  }

  _getTimeDifferenceInMinutes(dateFrom, dateTo) {
    const fromMinutes = dateFrom.getMinutes() + dateFrom.getHours() * 60;
    const toMinutes = dateTo.getMinutes() + dateTo.getHours() * 60;
    return fromMinutes - toMinutes;
  }

  createNewSlot(slot, weekday, fullDay = false) {
    if (!this.availability.unavailabilities[weekday]) {
      this.availability.unavailabilities[weekday] = [];
    }

    let dtstart = new Date(slot.dtstart);
    let dtend = new Date(slot.dtend);

    if (slot.halfEnd) {
      dtstart = this.Utils.addMinutes(dtstart, 30);
      dtend = this.Utils.addMinutes(dtend, 30);
    }

    if (slot.halfStart) {
      dtstart = this.Utils.addMinutes(dtstart, -30);
      dtend = this.Utils.addMinutes(dtend, -30);
    }

    const d1 = this._getDayFromWeekday(this.dateFilter(dtstart, 'EEEE'));
    const d2 = this._getDayFromWeekday(weekday);
    dtstart = this.Utils.addDays(dtstart, d2 - d1);
    dtend = this.Utils.addDays(dtend, d2 - d1);

    const { visibleHoursStart, visibleHoursEnd } = this.Authentication.user.orgSettings;
    const { visibleStart } = this.Utils.getBusinessDayForCalendar(
      visibleHoursStart,
      visibleHoursEnd,
      dtstart
    );

    if (!fullDay && this.Utils.getRealDate(dtstart) < this.Utils.getRealDate(visibleStart)) {
      dtstart = this.Utils.addDays(dtstart, 1);
    }

    if (!fullDay && this.Utils.getRealDate(dtend) <= this.Utils.getRealDate(visibleStart)) {
      dtend = this.Utils.addDays(dtend, 1);
    }

    this.availability.unavailabilities[weekday].push({
      dtstart,
      dtend,
      fullDay,
    });

    this.notifyChange();
  }

  removeSlot(slot, weekday) {
    this.availability.unavailabilities[weekday] = this.availability.unavailabilities[
      weekday
    ].filter((a) => a !== slot);
    this.notifyChange();
  }

  toggleAllDay(weekday) {
    if (!this.availability.unavailabilities[weekday]) {
      this.availability.unavailabilities[weekday] = [];
    }
    const weekdayUnavailabilities = this.availability.unavailabilities[weekday];

    /* toggle off */
    if (weekdayUnavailabilities.length === 1 && weekdayUnavailabilities[0].fullDay) {
      weekdayUnavailabilities.length = 0;
    } else {
      /* toggle on */
      weekdayUnavailabilities.length = 0;
      this.createNewSlot(this._getFullDaySlot(), weekday, true);
    }

    this.notifyChange();
  }

  changeDuration(hourArg, minute, boundary, slot) {
    const MILLISECONDS_IN_DAY = 24 * 3600 * 1000;
    const hour = hourArg % 24;
    delete slot._tempLabel;

    const { visibleHoursStart, visibleHoursEnd } = this.Authentication.user.orgSettings;
    const { visibleStart, visibleEnd } = this.Utils.getVisibleHoursBoundaries(
      visibleHoursStart,
      visibleHoursEnd,
      slot.dtstart
    );

    if (boundary === 'left') {
      slot.dtstart.setHours(hour);
      slot.dtstart.setMinutes(minute);
    } else {
      slot.dtend.setHours(hour);
      slot.dtend.setMinutes(minute);
    }

    if (slot.dtend - slot.dtstart > MILLISECONDS_IN_DAY) {
      if (boundary === 'left') {
        slot.dtstart = this.Utils.addDays(slot.dtstart, 1);
      } else {
        slot.dtend = this.Utils.addDays(slot.dtend, -1);
      }
    }

    if (slot.dtstart < visibleStart) {
      slot.dtstart.setHours(visibleStart.getHours());
      slot.dtstart.setMinutes(visibleStart.getMinutes());
    }

    if (slot.dtend > visibleEnd) {
      slot.dtend.setHours(visibleEnd.getHours());
      slot.dtend.setMinutes(visibleEnd.getMinutes());
    }

    if (
      slot.dtstart.getTime() === visibleStart.getTime() &&
      slot.dtend.getTime() === visibleEnd.getTime()
    ) {
      slot.fullDay = true;
      slot.dtstart.setHours(11);
      slot.dtstart = this.Utils.addHours(slot.dtstart, -slot.dtstart.getTimezoneOffset() / 60);
      slot.dtend = this.Utils.addMinutes(new Date(slot.dtstart), 1);
    }

    if (slot.dtstart >= slot.dtend) {
      if (boundary === 'left') {
        slot.dtstart = this.Utils.addDays(slot.dtstart, -1);
      } else {
        slot.dtend = this.Utils.addDays(slot.dtend, 1);
      }
    }

    this.notifyChange();
  }

  previewChangeDuration(hourArg, minute, boundary, slot) {
    const hour = hourArg % 24;

    if (slot.fullDay) {
      const { visibleHoursStart, visibleHoursEnd } = this.Authentication.user.orgSettings;
      const { visibleStart, visibleEnd } = this.Utils.getVisibleHoursBoundaries(
        visibleHoursStart,
        visibleHoursEnd,
        slot.dtstart
      );
      slot.fullDay = false;

      slot.dtstart = visibleStart;
      slot.dtend = visibleEnd;
    }

    if (boundary === 'left') {
      let dtstart = new Date(slot.dtstart.getTime());
      dtstart.setHours(hour, minute, 0, 0);

      if (dtstart >= slot.dtend) {
        dtstart = this.Utils.addDays(dtstart, -1);
      }

      slot._tempLabel = this._getUnavailableLabel(dtstart, slot.dtend);
    } else {
      let dtend = new Date(slot.dtstart.getTime());
      dtend.setHours(hour, minute, 0, 0);

      if (slot.dtstart >= dtend) {
        dtend = this.Utils.addDays(dtend, 1);
      }

      slot._tempLabel = this._getUnavailableLabel(slot.dtstart, dtend);
    }
  }

  clearUnavailability() {
    this.availability.unavailabilities = {};
    this.availability.details.until = null;
    this.editSet();
    this.notifyChange();
  }

  hasUnavailability() {
    if (!this.availability || !this.availability.unavailabilities) {
      return false;
    }
    const keys = Object.keys(this.availability.unavailabilities);

    return keys.some(
      (k) => this.availability.unavailabilities[k] && this.availability.unavailabilities[k].length
    );
  }

  getDisplayLabel(slot) {
    if (slot._tempLabel) {
      return slot._tempLabel;
    }

    if (slot.fullDay) {
      return 'Unavailable all day';
    }

    return this._getUnavailableLabel(slot.dtstart, slot.dtend);
  }

  editSet() {
    this.availability._editing = true;
    if (this.Authentication.user.isManager) {
      this.notifyChange();
    }
  }

  notifyChange() {
    if (this.singleSetForm) {
      this.singleSetForm.$setDirty();
    }

    this.availability._edited = true;
  }

  approve() {
    this.AvailabilityUtils.approveUnavailabilitySet(this.availability);
  }

  _getDayFromWeekday(weekday) {
    const day = this.sdk('constants').WEEKDAYS.indexOf(weekday) + 1;
    if (!day) {
      throw new Error(`unexpected weekday: ${weekday}`);
    }

    return day;
  }

  _rotateLeftWithCarry(array) {
    return [...array.slice(1), array[0]];
  }

  _getNewSlot(startHour, minute = 0, durationInMinutes = 60, date = new Date()) {
    const start = this.Utils.getRealDate(date);
    this._setHour(start, startHour, minute);
    const end = this.Utils.addMinutes(start, durationInMinutes);

    return {
      dtstart: start,
      dtend: end,
    };
  }

  _getFullDaySlot() {
    let date = new Date();
    this._setHour(date, 11);
    date = this.Utils.addHours(date, -date.getTimezoneOffset() / 60);

    return {
      dtstart: date,
      dtend: new Date(date),
    };
  }

  _calculateMinutes(date) {
    return Number(this.dateFilter(date, 'HH')) * 60 + Number(this.dateFilter(date, 'mm'));
  }

  _setHour(date, hours = 0, minutes = 0, seconds = 0, milliseconds = 0) {
    date.setHours(hours);
    date.setMinutes(minutes);
    date.setSeconds(seconds);
    date.setMilliseconds(milliseconds);
  }

  _getUnavailableLabel(dtstart, dtend) {
    const fromTo = this.sdk('model-extensions/getBaseDisplayLabel')(
      dtstart,
      dtend,
      this.Authentication.user.settings.usTimeFormat
    );
    const durationInMinutes = (dtend - dtstart) / 1000 / 60;
    const decimalFormat = this.Utils.getDecimalFormat();
    return `${fromTo} • ${this.Utils.getDisplayDuration(durationInMinutes, decimalFormat)}`;
  }

  _initNewSlots() {
    const slotsTemplate = this._initNewSlotsForDay();

    return this.weekdays.reduce((slots, weekday) => {
      slots[weekday] = slotsTemplate.map((slot) => ({ ...slot }));
      return slots;
    }, {});
  }

  _initNewSlotsForDay() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = this.Utils.addDays(today, 1);
    let passesMidnight = false;
    const { visibleHoursStart, visibleHoursEnd } = this.Authentication.user.orgSettings;
    const startMinutes = this.Utils.dateFromHours(visibleHoursStart).getMinutes();
    const endMinutes = this.Utils.dateFromHours(visibleHoursEnd).getMinutes();

    return this.visibleHours.slice(0, -1).map((hour, index, arr) => {
      if (!hour && index) {
        passesMidnight = true;
      }

      const date = passesMidnight ? tomorrow : today; // whenever midnight is between other hours - we're dealing with 2 dates in one business day

      let minute = 0;
      let duration = 60;
      if (!index && startMinutes) {
        minute = startMinutes;
        duration = 60 - startMinutes;
      }

      if (index === arr.length - 1 && endMinutes) {
        duration = endMinutes;
      }

      return this._getNewSlot(hour, minute, duration, date);
    });
  }

  _initHours() {
    const properHours = this.ShiftHeaderUtils.getProperHours();
    // Because availability grid renders diffrently then shifts we have to make sure we have room for minutes on last hour
    const lastHour = properHours[properHours.length - 1] + 1;
    properHours.push(lastHour);
    return this.ShiftHeaderUtils.getFormattedHours(properHours);
  }

  cancelEdit() {
    if (this.availability.availabilityId) {
      this.availability.reset();
    } else {
      this.deleteSet();
    }
  }

  getFromDatepickerOptions() {
    const options = {};

    if (!this.Authentication.user.isManager) {
      options.minDate = new Date();
      if (this.availability.availabilityId && this.availability.details.start < options.minDate) {
        options.minDate = this.availability.details.start;
      }
    }

    return options;
  }
}

export default {
  templateUrl: manageAvailabilityComponentUrl,
  controller: ManageAvailabilityCtrl,
  controllerAs: 'ctrl',
  bindings: {
    availability: '=src',
    readOnly: '<',
    isEditing: '<',
    isEditingAny: '<',
    user: '<',
    loading: '<',
    copySet: '&onCopy',
    deleteSet: '&onDelete',
    denySet: '&onReject',
    singleSet: '<',
  },
  require: {
    form: '?^form',
  },
};
